import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import type { User } from '../../../user/models/user';
import type { UserState } from '../../../user/store';
import { selectCurrentUser } from '../../../user/store';
import { Encryption } from '../../utils/encryption.util';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private store = inject<Store<UserState>>(Store);

  currentUser: User | undefined;
  encryption: Encryption | undefined;

  init(): void {
    this.setCurrentUser();
  }

  setEncryption(encryptionKey: string): void {
    if (!encryptionKey) {
      return;
    }
    void Encryption.create(encryptionKey).then(enc => (this.encryption = enc));
  }

  setCurrentUser(): void {
    this.store.select(selectCurrentUser).subscribe(user => {
      this.currentUser = user;
      if (user?.frontend_localstorage_key) {
        this.setEncryption(user.frontend_localstorage_key);
      }
    });
  }

  setEncryptedCurrentUserValue(key: string, value: string): void {
    if (!key) {
      return;
    }
    const currentUserKey = this.getCurrentUserKey(key);
    if (!currentUserKey) {
      return;
    }
    this.setEncryptedValue(currentUserKey, value);
  }

  getDecryptedCurrentUserValue(key: string): Promise<string> {
    const emptyValue = new Promise<string>(resolve => resolve(''));
    if (!key) {
      return emptyValue;
    }
    const currentUserKey = this.getCurrentUserKey(key);
    if (!currentUserKey) {
      return emptyValue;
    }
    return this.getDecryptedValue(currentUserKey);
  }

  setEncryptedValue(key: string, value: string): void {
    if (!this.encryption) {
      this.showEncryptionInitError();
      return;
    }
    if (!key) {
      return;
    }
    void this.encryption.encrypt(value).then(encryptedValue => {
      localStorage.setItem(key, encryptedValue);
    });
  }

  getDecryptedValue(key: string): Promise<string> {
    const emptyValue = new Promise<string>(resolve => resolve(''));
    if (!this.encryption) {
      this.showEncryptionInitError();
      return emptyValue;
    }
    if (!key) {
      return emptyValue;
    }
    const localStorageValue = localStorage.getItem(key) || '';
    if (!localStorageValue) {
      return emptyValue;
    }
    try {
      return this.encryption.decrypt(localStorageValue);
    } catch {
      return emptyValue;
    }
  }

  setCurrentUserItem(key: string, value: string): void {
    if (!key) {
      return;
    }
    const currentUserKey = this.getCurrentUserKey(key);
    if (!currentUserKey) {
      return;
    }
    localStorage.setItem(currentUserKey, value);
  }

  getCurrentUserItem(key: string): unknown {
    if (!key) {
      return '';
    }
    const currentUserKey = this.getCurrentUserKey(key);
    if (!currentUserKey) {
      return '';
    }
    const localStorageValue = localStorage.getItem(currentUserKey) || '';
    return this.parsedValue(localStorageValue);
  }

  getCurrentUserKey(key: string): string | undefined {
    const currentUserId = this.currentUser?.id;
    if (!currentUserId) {
      return undefined;
    }
    return `${currentUserId}-${key}`;
  }

  removeCurrentUserItem(key: string): void {
    const currentUserKey = this.getCurrentUserKey(key);
    if (!currentUserKey) {
      return;
    }
    localStorage.removeItem(currentUserKey);
  }

  setItem(key: string, value: string): void {
    if (!key) {
      return;
    }
    localStorage.setItem(key, value);
  }

  getItem(key: string): unknown {
    if (!key) {
      return '';
    }
    const localStorageValue = localStorage.getItem(key) || '';
    return this.parsedValue(localStorageValue);
  }

  parsedValue(value: string): unknown {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  removeItem(key: string): void {
    localStorage.removeItem(key);
  }

  showEncryptionInitError(): void {
    // eslint-disable-next-line no-console
    console.error('Error: Encryption was not initialized');
  }
}
